import { X, RefreshCw, AlertTriangle, Loader2 } from 'lucide-react';
import { Spinner } from 'react-bootstrap';
import type { RegenerateInvoicePreview } from '@/features/contracts/api/contractApi';
import { formatPrice } from '@/features/contracts/utils/contractFormatter';

interface RegenerateInvoiceModalProps {
  isOpen: boolean;
  preview: RegenerateInvoicePreview | null;
  isLoadingPreview: boolean;
  previewError: string | null;
  isConfirming: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const formatFrenchDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
};

export const RegenerateInvoiceModal: React.FC<RegenerateInvoiceModalProps> = ({
  isOpen,
  preview,
  isLoadingPreview,
  previewError,
  isConfirming,
  onClose,
  onConfirm,
}) => {
  if (!isOpen) return null;

  const canConfirm = !!preview && !isLoadingPreview && !isConfirming && !previewError;

  return (
    <div
      className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[100] tw-p-4"
      onClick={isConfirming ? undefined : onClose}
    >
      <div
        className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-2xl tw-w-full tw-max-h-[90vh] tw-overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-p-4 md:tw-p-6 tw-border-b tw-border-gray-200 tw-sticky tw-top-0 tw-bg-white tw-z-10">
          <div className="tw-flex tw-items-center tw-gap-3">
            <h2 className="tw-text-xl tw-font-bold tw-text-gray-900">
              Régénérer la facture échéancier
            </h2>
            <span className="tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-bg-amber-100 tw-text-amber-700 tw-rounded">
              Admin
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isConfirming}
            className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors disabled:tw-opacity-50"
            aria-label="Fermer"
          >
            <X className="tw-w-5 tw-h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="tw-p-4 md:tw-p-6 tw-space-y-4">
          {isLoadingPreview && (
            <div className="tw-flex tw-items-center tw-justify-center tw-py-12 tw-gap-3">
              <Spinner animation="border" size="sm" />
              <span className="tw-text-sm tw-text-gray-500">
                Chargement du récapitulatif…
              </span>
            </div>
          )}

          {previewError && !isLoadingPreview && (
            <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-4 tw-flex tw-items-start tw-gap-3">
              <AlertTriangle className="tw-w-5 tw-h-5 tw-text-red-600 tw-flex-shrink-0 tw-mt-0.5" />
              <div className="tw-text-sm tw-text-red-700">
                <p className="tw-font-semibold tw-mb-1">Régénération impossible</p>
                <p>{previewError}</p>
              </div>
            </div>
          )}

          {preview && !isLoadingPreview && !previewError && (
            <>
              {/* Info banner */}
              <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-3 tw-text-sm tw-text-blue-800">
                Le PDF actuel sera <strong>écrasé à la même URL</strong>. Le numéro
                de facture est conservé. Aucun nouveau Document n'est créé.
              </div>

              {/* Récap tableau */}
              <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
                <table className="tw-w-full tw-text-sm">
                  <tbody>
                    <tr className="tw-border-b tw-border-gray-100">
                      <td className="tw-px-4 tw-py-2.5 tw-text-gray-600 tw-w-1/2">
                        Contrat
                      </td>
                      <td className="tw-px-4 tw-py-2.5 tw-font-medium tw-text-gray-900">
                        {preview.contractLabel}
                      </td>
                    </tr>
                    <tr className="tw-border-b tw-border-gray-100">
                      <td className="tw-px-4 tw-py-2.5 tw-text-gray-600">
                        Numéro de facture (conservé)
                      </td>
                      <td className="tw-px-4 tw-py-2.5 tw-font-mono tw-text-gray-900">
                        {preview.invoiceLabel}
                      </td>
                    </tr>
                    <tr className="tw-border-b tw-border-gray-100">
                      <td className="tw-px-4 tw-py-2.5 tw-text-gray-600">Société</td>
                      <td className="tw-px-4 tw-py-2.5 tw-text-gray-900">
                        {preview.companyCode}
                      </td>
                    </tr>
                    <tr className="tw-border-b tw-border-gray-100">
                      <td className="tw-px-4 tw-py-2.5 tw-text-gray-600">
                        Nombre d'échéances
                      </td>
                      <td className="tw-px-4 tw-py-2.5 tw-text-gray-900">
                        {preview.iteration}
                      </td>
                    </tr>
                    <tr className="tw-border-b tw-border-gray-100 tw-bg-amber-50">
                      <td className="tw-px-4 tw-py-2.5 tw-text-gray-700">
                        Montant par échéance
                      </td>
                      <td className="tw-px-4 tw-py-2.5 tw-font-bold tw-text-amber-900">
                        {formatPrice(preview.monthlyTtc)}
                      </td>
                    </tr>
                    <tr className="tw-border-b tw-border-gray-100">
                      <td className="tw-px-4 tw-py-2.5 tw-text-gray-600">
                        Total facturé sur la durée
                      </td>
                      <td className="tw-px-4 tw-py-2.5 tw-font-medium tw-text-gray-900">
                        {formatPrice(preview.totalTtc)}
                      </td>
                    </tr>
                    <tr className="tw-border-b tw-border-gray-100">
                      <td className="tw-px-4 tw-py-2.5 tw-text-gray-600">
                        1ʳᵉ échéance
                      </td>
                      <td className="tw-px-4 tw-py-2.5 tw-text-gray-900">
                        {formatFrenchDate(preview.firstChargeDate)}
                      </td>
                    </tr>
                    <tr className="tw-border-b tw-border-gray-100">
                      <td className="tw-px-4 tw-py-2.5 tw-text-gray-600">
                        Dernière échéance
                      </td>
                      <td className="tw-px-4 tw-py-2.5 tw-text-gray-900">
                        {formatFrenchDate(preview.lastChargeDate)}
                      </td>
                    </tr>
                    <tr>
                      <td className="tw-px-4 tw-py-2.5 tw-text-gray-500 tw-text-xs">
                        Source Stripe
                      </td>
                      <td className="tw-px-4 tw-py-2.5 tw-text-gray-500 tw-text-xs tw-font-mono">
                        {preview.stripeInvoiceId}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Warning */}
              <div className="tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-lg tw-p-3 tw-text-sm tw-text-amber-900 tw-flex tw-items-start tw-gap-2">
                <AlertTriangle className="tw-w-4 tw-h-4 tw-text-amber-600 tw-flex-shrink-0 tw-mt-0.5" />
                <span>
                  Action audit-loguée. Le client conserve l'URL existante du PDF
                  (envoyée par email à la souscription).
                </span>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="tw-flex tw-justify-end tw-gap-3 tw-p-4 md:tw-p-6 tw-border-t tw-border-gray-200 tw-bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={isConfirming}
            className="tw-px-4 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white hover:tw-bg-gray-50 disabled:tw-opacity-50 tw-transition-colors"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-text-white tw-bg-amber-600 hover:tw-bg-amber-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
          >
            {isConfirming ? (
              <>
                <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
                Régénération…
              </>
            ) : (
              <>
                <RefreshCw className="tw-w-4 tw-h-4" />
                Régénérer
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
